import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { AccountHeader } from '@/components/account-nav'
import { Button } from '@/components/ui/button'
import {
  ApiError,
  billingExpiresAt,
  billingReason,
  billingReasonText,
  deleteAccount,
  formatCents,
  getBillingStatus,
  getMe,
  postChangePassword,
  postLogout,
  type AuthUser,
  type BillingStatus,
} from '@/lib/kwykedit-api'

export const Route = createFileRoute('/routes/account')({
  component: AccountPage,
})

type State =
  | { kind: 'loading' }
  | { kind: 'signed-out' }
  | { kind: 'error'; message: string }
  | { kind: 'ok'; me: AuthUser; status: BillingStatus | null }

function fmtDate(sec: number | null): string {
  if (!sec) return '—'
  return new Date(sec * 1000).toLocaleString()
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (pending) return
    if (next !== confirm) {
      setError('New passwords do not match.')
      return
    }
    setPending(true)
    setError(null)
    setSaved(false)
    void postChangePassword(current, next)
      .then(() => {
        setSaved(true)
        setCurrent('')
        setNext('')
        setConfirm('')
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not change your password.')
      })
      .finally(() => setPending(false))
  }

  const inputClassName =
    'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none'

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <h2 className="mb-3 font-semibold">Change password</h2>
      <form onSubmit={submit} className="space-y-3">
        <label className="block text-xs text-muted-foreground">
          Current password
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            className={`mt-1 ${inputClassName}`}
          />
        </label>
        <label className="block text-xs text-muted-foreground">
          New password (8+ characters)
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            className={`mt-1 ${inputClassName}`}
          />
        </label>
        <label className="block text-xs text-muted-foreground">
          Confirm new password
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            className={`mt-1 ${inputClassName}`}
          />
        </label>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {saved && <p className="text-sm text-green-500">Password changed.</p>}
        <Button type="submit" disabled={pending}>
          {pending ? 'Saving…' : 'Change password'}
        </Button>
      </form>
    </div>
  )
}

function CloseAccountCard() {
  const navigate = useNavigate()
  const [confirming, setConfirming] = useState(false)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const close = () => {
    if (pending) return
    setPending(true)
    setError(null)
    void deleteAccount()
      .then(() => navigate({ to: '/' }))
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not close your account.')
      })
      .finally(() => setPending(false))
  }

  if (!confirming) {
    return (
      <>
        <p className="mb-3 text-sm text-muted-foreground">
          Stops PayPal billing immediately, locks the editor, and signs you out. Your
          email is kept on file so new-user offers cannot be claimed twice.
        </p>
        <Button variant="outline" onClick={() => setConfirming(true)}>
          Close my account…
        </Button>
      </>
    )
  }

  return (
    <>
      <p className="mb-3 text-sm font-medium">
        Last chance — closing stops billing and locks editing right away. Continue?
      </p>
      {error && <p className="mb-3 text-sm text-destructive">{error}</p>}
      <div className="flex gap-2">
        <Button variant="destructive" disabled={pending} onClick={close}>
          {pending ? 'Closing…' : 'Yes, close and stop billing'}
        </Button>
        <Button variant="outline" disabled={pending} onClick={() => setConfirming(false)}>
          Keep my account
        </Button>
      </div>
    </>
  )
}

function AccountPage() {
  const navigate = useNavigate()
  const [state, setState] = useState<State>({ kind: 'loading' })
  useEffect(() => {
    let live = true
    void (async () => {
      const me = await getMe()
      const status = await getBillingStatus().catch(() => null)
      if (live) setState({ kind: 'ok', me, status })
    })().catch((err: unknown) => {
      if (!live) return
      if (err instanceof ApiError && err.status === 401) {
        setState({ kind: 'signed-out' })
      } else {
        setState({
          kind: 'error',
          message: err instanceof ApiError ? err.message : 'Could not load your account.',
        })
      }
    })
    return () => {
      live = false
    }
  }, [])

  const handleLogout = () => {
    void postLogout()
      .catch(() => undefined)
      .finally(() => {
        void navigate({ to: '/' })
      })
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AccountHeader />
      <div className="mx-auto max-w-2xl px-6 py-12">
        <h1 className="mb-6 text-2xl font-bold">Account</h1>

        {state.kind === 'loading' && (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}

        {state.kind === 'signed-out' && (
          <div className="rounded-xl border border-border bg-card p-6 text-center">
            <p className="mb-4 text-sm text-muted-foreground">You are not signed in.</p>
            <div className="flex justify-center gap-2">
              <Button asChild>
                <Link to="/login">Log in</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/signup">Sign up</Link>
              </Button>
            </div>
          </div>
        )}

        {state.kind === 'error' && (
          <p className="text-sm text-destructive">{state.message}</p>
        )}

        {state.kind === 'ok' && (
          <div className="space-y-4">
            <div className="rounded-xl border border-border bg-card p-6">
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Email</dt>
                  <dd className="font-medium">{state.me.email}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Member since</dt>
                  <dd>{fmtDate(state.me.created_at)}</dd>
                </div>
                {state.me.is_admin && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Role</dt>
                    <dd>
                      Admin ·{' '}
                      <Link to="/admin" className="underline hover:opacity-80">
                        Open admin
                      </Link>
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            <div className="rounded-xl border border-border bg-card p-6">
              <h2 className="mb-3 font-semibold">Subscription</h2>
              {!state.status ? (
                <p className="text-sm text-muted-foreground">
                  Could not load billing status.{' '}
                  <Link to="/pricing" className="underline hover:opacity-80">
                    View pricing
                  </Link>
                </p>
              ) : (
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Status</dt>
                    <dd className="font-medium">
                      {state.status.has_access ? 'Active' : 'No access'} ·{' '}
                      {billingReason(state.status)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Plan</dt>
                    <dd>{formatCents(state.status.base_cents)}/mo</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Renews / expires</dt>
                    <dd>{fmtDate(billingExpiresAt(state.status))}</dd>
                  </div>
                  <p className="pt-1 text-sm text-muted-foreground">
                    {billingReasonText(state.status)}
                  </p>
                </dl>
              )}
              <div className="mt-4 flex gap-2">
                <Button asChild>
                  <Link to="/pricing">Manage subscription</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/projects">Open editor</Link>
                </Button>
              </div>
            </div>

            <ChangePasswordCard />

            <div className="rounded-xl border border-destructive/40 bg-card p-6">
              <h2 className="mb-1 font-semibold">Close account</h2>
              <CloseAccountCard />
            </div>

            <Button variant="ghost" onClick={handleLogout}>
              Log out
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
